/**
 *
 * DynamicAxiosSelect
 *
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// styles
import './style.scss';

// antd component
import { Form, Select, Spin, Empty, Tag } from 'antd';
const { Option, OptGroup } = Select;

// assets
//import CustomIcons from 'components/CustomIcons';
import { useTranslation} from "react-i18next";

// utils
import { sortByKey } from 'utils/helpers/helper.general';
import {
  filterGroupedOptionsByValue,
  getUniqueGroupedOptions,
} from 'utils/helpers/helper.filter-sort';
import api from 'utils/api';
// hooks
import { useAsync, useRefState } from 'utils/hooks';

function DynamicAxiosSelect({
  mode,
  sortBy,
  groupped,
  method,
  endponit,
  endponitSend,
  item,
  value,
  onlyRead,
  placeholder,
  isSearch,
  onSearch,
  showAsTag,
  maxTagCount,
  extra,
  help,
  // actions
  tagRender,
  itemCallback,
  onSelect,
  onChange,
  getResponse,
  className,
}) {
  const { t } = useTranslation();
  const childClassNames = classNames('dyncamic-axios-select-wrapper', className);

  const { isLoading, isSuccess, run, data, reset } = useAsync();

  const [searchVal, searchValRef, setSearchVal] = useRefState('');
  const [listOptions, setListOptions] = useState([]);

  const [rootData, setRootData] = useState([]);
  const [sendingParams, sendingParamsRef, setSendingParams] = useRefState(
    endponitSend || item.endponitSend,
  );

  const [firstLoad, firstLoadRef, setFirstLoad] = useRefState(
    endponitSend?.firstLoad || true,
  );

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {

    if (!isLoading && (endponit || (endponit && sendingParamsRef?.current))) {
      if (method == 'GET') {
        run(
          api.route.fetchAll(endponit, {
            ...sendingParamsRef?.current,
            firstLoad: firstLoadRef.current,
          }),
        );
      } else {
        run(
          api.route.findAll(endponit, {
            ...sendingParamsRef?.current,
            firstLoad: firstLoadRef.current,
          }),
        );
      }
    }
    return () => {
      setSearchVal('');
      setListOptions([]);
      setRootData([]);
      setFirstLoad(true);
      reset();
    };
  }, []);

  useEffect(() => {
    if (endponitSend) {
      setSendingParams(endponitSend);
    } else {
      setSendingParams(item.endponitSend);
    }
  }, [item, endponitSend, item.endponitSend]);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    if (
      !isLoading &&
      !firstLoadRef.current &&
      (endponit || (endponit && sendingParamsRef?.current))
    ) {
      if (method == 'GET') {
        run(
          api.route.fetchAll(endponit, {
            ...sendingParamsRef?.current,
            firstLoad: firstLoadRef.current,
          }),
        );
      } else {
        run(
          api.route.findAll(endponit, {
            ...sendingParamsRef?.current,
            firstLoad: firstLoadRef.current,
          }),
        );
      }
    }
  }, [endponit, sendingParamsRef]);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    if (!!data) {
      const listData = data?.data || data;
      getResponse !== undefined && getResponse({ isLoading, isSuccess, listData });
      const optioins =
        groupped == undefined ? listData?.map(elem => itemCallback(elem)) : listData;
      let mainOpt = optioins?.filter(el => el !== null && el) || [];
      // sort options
      sortBy !== undefined && mainOpt && mainOpt?.sort(sortByKey(sortBy));
      firstLoadRef.current && setRootData(mainOpt);
      if (searchValRef.current.length != 0) {
        mainOpt = filterGroupedOptionsByValue(mainOpt, searchValRef.current);
      }
      setListOptions(mainOpt);
      setFirstLoad(false);
    }
  }, [data]);

  const multipleOptions = mode && {
    mode: mode,
    maxTagCount: maxTagCount || 3, // 'responsive'
  };

  mode && !showAsTag && tagRender && (multipleOptions.tagRender = tagRender);
  mode && showAsTag &&
    (multipleOptions.tagRender = props => {
      const isClosable = onlyRead ? false : props.closable;
      const label = props.label;
      let labelClass = 'default';
      // generate class for communication audience type
      if (label && label?.indexOf(':') != -1 && data?.hiddenChain == 'audience') {
        const labelArr = label.split(':'),
          firstPart = labelArr[0].trim(),
          firstPartLower = firstPart.toLowerCase(),
          secondPart = labelArr[1].trim();
        switch (firstPartLower) {
          case 'classe':
            labelClass = 'class';
            break;
          case 'cours':
            labelClass = 'course';
            break;
          case 'etudiant':
            labelClass = 'student';
            break;
          case 'enseignant':
            labelClass = 'teacher';
            break;
          case 'admin':
            labelClass = 'admin';
            break;
          case 'audience':
            labelClass = 'audience';
            break;
          default:
            labelClass = 'default';
            break;
        }
      }
      return (
        <Tag
          className={`multiple-label-list ${labelClass}`}
          closable={isClosable}
          onClose={props.onClose}
        >
          {props?.label || props?.title}
        </Tag>
      );
    });

  const selectLocale = {
    emptyText: (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('errors.noData')}
      />
    ),
  };

  const onSearchCallback = input => {
    setSearchVal(input);
    if (onSearch !== undefined) {
      const newOpt = filterGroupedOptionsByValue(listOptions, input);
      if (
        (searchVal != input && input.length != 0) ||
        (newOpt && newOpt.length <= 0)
      ) {
        const newObjSend = {
          ...endponitSend,
          firstLoad: false,
          value: input,
        };
        !isLoading && run(api.route.findAll(endponit, newObjSend));
      } else {
        if (newOpt && newOpt.length > 0 && input.length != 0) {
          setListOptions(newOpt);
        } else {
          setListOptions(rootData);
        }
      }
      onSearch(input);
    }
  };

  const resetSearch = () => {
    setSearchVal('');
    const dumpOption = [...rootData, ...listOptions];
    const uniqueOption = getUniqueGroupedOptions(dumpOption);
    setListOptions(uniqueOption);
  };

  const onSelectCallback = (LabeledValue, option) => {
    resetSearch();
    onSelect !== undefined && onSelect(LabeledValue, option);
  };

  return (
    <>
      {data && (
        <Form.Item
          key={item?.name + '-item'}
          label={item?.displayLabel ? item?.label : ''}
          name={item?.name}
          className={childClassNames}
          initialValue={value || item.value || (mode && tagRender) ? [] : null}
          rules={[
            {
              required: item?.required || false,
              message: t('inputs.selectRequiredField'),
           //   validateTrigger: 'onSubmit',
            },
          ]}
          extra={extra}
          help={help}
        >
          <Select
            locale={selectLocale}
            placeholder={placeholder || t('inputs.select')}
            optionFilterProp="children"
            onSelect={onSelectCallback}
            onChange={onChange}
            showArrow
            showSearch={isSearch}
            searchValue={searchVal}
            onSearch={onSearchCallback}
            onBlur={resetSearch}
            optionLabelProp={item.optionLabelProp || 'label'}
            loading={isLoading}
            getPopupContainer={trigger => trigger.parentElement}
            notFoundContent={
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('errors.noData')}
              />
            }
            filterOption={
              onSearch == undefined &&
              ((input, option) => {
                return (
                  (option?.title &&
                    option?.title.toLowerCase().indexOf(input.toLowerCase()) >=
                      0) ||
                  (option?.label &&
                    option?.label.toLowerCase().indexOf(input.toLowerCase()) >=
                      0)
                );
              })
            }
            {...multipleOptions}
          >
            {listOptions?.map((option, i) => {
              if (option?.group) {
                return (
                  <OptGroup
                    key={`${option?.group}-${option?.key}`}
                    label={option?.label || option?.group || option?.key}
                  >
                    {option?.options?.map((option, i) => (
                      <Option
                        {...option}
                        key={option?.key || option?.value}
                        value={option?.value || option?.key}
                        label={option?.label || option?.title || option?.value}
                      >
                        {
                          <span
                            className="option"
                            dangerouslySetInnerHTML={{
                              __html: option?.label || option?.title || option?.value,
                            }}
                          />
                        }
                      </Option>
                    ))}
                  </OptGroup>
                );
              } else {
                return (
                  <Option
                    {...option}
                    key={option?.key || option?.value}
                    value={option?.value || option?.key}
                    label={option?.label || option?.title || option?.value}
                  >
                    {
                      <span
                        className="option"
                        dangerouslySetInnerHTML={{
                          __html:
                            option?.title || option?.label || option?.value,
                        }}
                      />
                    }
                  </Option>
                );
              }
            })}
          </Select>
        </Form.Item>
      )}
      {isLoading && !data && <Spin />}
    </>
  );
}

DynamicAxiosSelect.defaultProps = {
  isSearch: true,
  method: 'GET',
  value: []
};
DynamicAxiosSelect.propTypes = {
  sortBy: PropTypes.string,
  method: PropTypes.string,
  isSearch: PropTypes.bool,
};

export default DynamicAxiosSelect;
